import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { apiClient, endpoints, API_BASE_URL } from '../api';
import { 
  IndianRupee, 
  ShoppingCart, 
  Package, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  Clock,
  Truck,
  CheckCircle,
  XCircle,
  Eye,
  RefreshCw,
  Edit3,
  Trash2,
  FileText
} from 'lucide-react';
// (removed duplicate React and api imports)

const FileIcon = ({ mime }) => {
  if ((mime || '').startsWith('image/')) {
    return <ImagePlaceholder />;
  }
  if ((mime || '').includes('pdf')) {
    return <FileText size={16} />;
  }
  return <FileText size={16} />;
};

const ImagePlaceholder = () => (
  <span style={{ width: 16, height: 16, display: 'inline-block', background: '#c7d2fe', borderRadius: 2 }} />
);
import './Dashboard.css';

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [kycStatus, setKycStatus] = useState('DRAFT');
  const [kycNotes, setKycNotes] = useState('');
  const [vendorId, setVendorId] = useState(null);
  const [kycJustSubmitted, setKycJustSubmitted] = useState(false);
  const [kycNotification, setKycNotification] = useState(null);

  const [weeklyRevenue, setWeeklyRevenue] = useState({ current: 0, previous: 0, change: 0, dailyBreakdown: [] });

  const [ordersByStatus, setOrdersByStatus] = useState({
    pending: { count: 0, amount: 0, changeCount: 0, changeAmount: 0 },
    shipped: { count: 0, amount: 0, changeCount: 0, changeAmount: 0 },
    delivered: { count: 0, amount: 0, changeCount: 0, changeAmount: 0 }
  });

  const [inventoryAlerts, setInventoryAlerts] = useState([]);

  const [payoutSummary, setPayoutSummary] = useState({ totalEarnings: 0, pendingPayout: 0, lastPayout: 0, nextPayoutDate: '', payoutMethod: '' });
  const [kpis, setKpis] = useState({ totalProducts: 0, activeOrders: 0, pendingShipments: 0, totalRevenue: 0, accountBalance: 0 });
  const [kycDocs, setKycDocs] = useState([]);
  const docLabelMap = {
    gst: 'GST',
    fssai: 'FSSAI',
    shopLicense: 'Shop/Trade License',
    pan: 'PAN',
    aadhaar: 'AADHAAR',
    bankProof: 'Bank Proof'
  };

  // Initialize KYC status from cached vendor data to avoid showing Draft momentarily
  useEffect(() => {
    try {
      const stored = localStorage.getItem('vendorData');
      const parsed = stored ? JSON.parse(stored) : null;
      const status = String(parsed?.status || '').toUpperCase();
      if (status) {
        const normalized = status === 'UNDER_REVIEW' ? 'IN_REVIEW' : (['VERIFIED','ACTIVE'].includes(status) ? 'APPROVED' : status);
        setKycStatus(normalized);
      }
    } catch (_) {}
  }, []);

  const handleRefresh = () => {
    loadData();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Pending': 'status-pending',
      'Shipped': 'status-shipped',
      'Delivered': 'status-delivered',
      'Low Stock': 'status-low',
      'Out of Stock': 'status-critical'
    };
    return statusClasses[status] || 'status-default';
  };

  const getPriorityColor = (priority) => {
    return priority === 'high' ? '#ef4444' : '#f59e0b';
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      // Load vendorId from localStorage for KYC status
      const stored = localStorage.getItem('vendorData');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed?.id) {
            setVendorId(parsed.id);
          }
        } catch (e) {}
      }
      const token = localStorage.getItem('token');
      const [revRes, statusRes, alertsRes, payoutRes] = await Promise.all([
        fetch('/api/metrics/weekly-revenue', { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch('/api/metrics/orders-status', { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch('/api/alerts', { headers: { 'Authorization': `Bearer ${token}` }}),
        fetch('/api/payouts/summary', { headers: { 'Authorization': `Bearer ${token}` }})
      ]);
      if (revRes.ok) setWeeklyRevenue(await revRes.json());
      if (statusRes.ok) setOrdersByStatus(await statusRes.json());
      if (alertsRes.ok) {
        const alerts = await alertsRes.json();
        const formatted = alerts.map(a => ({
          id: a.id,
          product: a.product_name,
          currentStock: a.current_stock,
          minStock: a.threshold_value,
          status: a.alert_type === 'out_of_stock' ? 'Out of Stock' : 'Low Stock',
          priority: a.alert_type === 'out_of_stock' ? 'high' : 'medium'
        }));
        setInventoryAlerts(formatted);
      }
      if (payoutRes.ok) setPayoutSummary(await payoutRes.json());

      // Load additional KPI sources
      try {
        const [productsRes, payoutPreviewRes] = await Promise.all([
          fetch(`/api/products?limit=1`, { headers: { 'Authorization': `Bearer ${token}` }}),
          fetch(`/api/payouts/preview`, { headers: { 'Authorization': `Bearer ${token}` }})
        ]);

        let totalProducts = 0;
        if (productsRes.ok) {
          const pr = await productsRes.json();
          totalProducts = Number(pr?.total || 0);
        }

        let accountBalance = 0;
        if (payoutPreviewRes.ok) {
          const pp = await payoutPreviewRes.json();
          accountBalance = Number(pp?.estimatedAmount || 0);
        }

        // Derive active orders and pending shipments from ordersByStatus (after it's set)
        // Use latest known values from local variables if available
        const ordersStatusData = await (async () => {
          try {
            const res = await fetch('/api/metrics/orders-status', { headers: { 'Authorization': `Bearer ${token}` }});
            if (res.ok) return res.json();
          } catch (_) {}
          return null;
        })();

        const statusData = ordersStatusData || (typeof ordersByStatus === 'object' ? ordersByStatus : {});
        const activeOrders = Number(statusData?.pending?.count || 0);
        const pendingShipments = Number(statusData?.shipped?.count || 0);

        // Total revenue from summary loaded above
        const totalRevenue = Number((await (async () => payoutRes.ok ? (await payoutRes.clone().json()) : payoutSummary))?.totalEarnings || 0);

        setKpis({ totalProducts, activeOrders, pendingShipments, totalRevenue, accountBalance });
      } catch (_) {}
      // Fetch KYC status and docs if vendorId is known
      try {
        const id = JSON.parse(localStorage.getItem('vendorData') || '{}').id;
        if (id) {
          const res = await apiClient.get(endpoints.kycOverallStatus(id));
          if (res.status === 200 && res.data?.success && res.data?.data?.vendor) {
            setKycStatus(res.data.data.vendor.status);
            setKycNotes(res.data.data.vendor.review_notes || '');
          }
          try {
            const docsRes = await apiClient.get(endpoints.kycDocuments(id));
            if (docsRes.status === 200 && docsRes.data?.success && Array.isArray(docsRes.data?.data)) {
              // Deduplicate by latest per documentType
              const latestByType = {};
              docsRes.data.data.forEach(d => {
                const key = (d.document_type || '').toLowerCase();
                if (!key) return;
                const current = latestByType[key];
                if (!current || new Date(d.uploaded_at) > new Date(current.uploaded_at)) {
                  latestByType[key] = d;
                }
              });
              const normalized = Object.values(latestByType).map(d => ({
                ...d,
                label: docLabelMap[(d.document_type || '').toLowerCase()] || (d.document_type || '').toUpperCase(),
                absoluteUrl: d.file_path?.startsWith('/') ? `${API_BASE_URL}${d.file_path}` : d.file_path
              }));
              setKycDocs(normalized);
            }
          } catch (e) {}
        }
      } catch (e) {
        // ignore KYC status fetch errors on dashboard
      }
      setLastUpdated(new Date());
    } catch (e) {
      console.error('Dashboard load error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { 
    loadData(); 
    try {
      if (localStorage.getItem('kycSubmitted') === '1') {
        setKycJustSubmitted(true);
        localStorage.removeItem('kycSubmitted');
      }
    } catch (e) {}
  }, []);

  const handleDeleteDoc = async (docId) => {
    try {
      const id = JSON.parse(localStorage.getItem('vendorData') || '{}').id;
      const token = localStorage.getItem('token');
      if (!id || !token) return;
      await apiClient.delete(endpoints.deleteKycDoc(id, docId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // refresh docs
      const docsRes = await apiClient.get(endpoints.kycDocuments(id));
      if (docsRes.status === 200 && docsRes.data?.success && Array.isArray(docsRes.data?.data)) {
        const latestByType = {};
        docsRes.data.data.forEach(d => {
          const key = (d.document_type || '').toLowerCase();
          if (!key) return;
          const current = latestByType[key];
          if (!current || new Date(d.uploaded_at) > new Date(current.uploaded_at)) {
            latestByType[key] = d;
          }
        });
        const normalized = Object.values(latestByType).map(d => ({
          ...d,
          label: docLabelMap[(d.document_type || '').toLowerCase()] || (d.document_type || '').toUpperCase(),
          absoluteUrl: d.file_path?.startsWith('/') ? `${API_BASE_URL}${d.file_path}` : d.file_path
        }));
        setKycDocs(normalized);
      }
    } catch (e) {
      console.error('Delete KYC doc error:', e);
      alert('Failed to delete document');
    }
  };

  // Realtime notifications for KYC status changes
  useEffect(() => {
    const socket = io(API_BASE_URL, { transports: ['websocket'] });
    const stored = localStorage.getItem('vendorData');
    let id = null;
    try { id = JSON.parse(stored || '{}').id; } catch (e) {}
    if (id) {
      socket.emit('join-vendor', id);
      socket.on('kyc-status-changed', (payload) => {
        setKycStatus((payload?.status === 'UNDER_REVIEW') ? 'IN_REVIEW' : (payload?.status || 'DRAFT'));
        setKycNotes(payload?.reviewNotes || '');
        // In-app banner notification
        const mapped = (payload?.status === 'UNDER_REVIEW') ? 'Under Review' : (payload?.status || '');
        const approvedSuffix = (payload?.softLaunch && (payload.softLaunch.goLiveAt || payload.softLaunch.mode || payload.softLaunch.notes))
          ? ` Activation details — ${payload.softLaunch.goLiveAt ? `Go-live: ${new Date(payload.softLaunch.goLiveAt).toLocaleString()}. ` : ''}${payload.softLaunch.mode ? `Mode: ${payload.softLaunch.mode}. ` : ''}${payload.softLaunch.notes ? `Notes: ${payload.softLaunch.notes}` : ''}`
          : ' You can accept orders immediately.';
        const message = (payload?.status === 'APPROVED')
          ? `Your account is approved.${approvedSuffix}`
          : `Your KYC is now ${mapped}${payload?.reviewNotes ? ` — ${payload.reviewNotes}` : ''}.`;
        setKycNotification({
          title: 'KYC Status Updated',
          message,
          type: (payload?.status === 'APPROVED') ? 'success' : (payload?.status === 'REJECTED') ? 'error' : 'info'
        });
        // Browser notification
        try {
          if (window.Notification && Notification.permission === 'granted') {
            new Notification('KYC Status Updated', { body: `Your KYC is now ${payload?.status}` });
          } else if (window.Notification && Notification.permission !== 'denied') {
            Notification.requestPermission();
          }
        } catch (e) {}
      });
    }
    return () => { socket.disconnect(); };
  }, []);

  return (
    <div className="dashboard">
      {/* KPI Cards */}
      <div className="widget kpi-widget">
        <div className="widget-content">
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-label">Total Products</div>
              <div className="kpi-value">{kpis.totalProducts}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Active Orders</div>
              <div className="kpi-value">{kpis.activeOrders}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Pending Shipments</div>
              <div className="kpi-value">{kpis.pendingShipments}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Total Revenue</div>
              <div className="kpi-value">{formatCurrency(kpis.totalRevenue)}</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Account Balance</div>
              <div className="kpi-value">{formatCurrency(kpis.accountBalance)}</div>
            </div>
          </div>
        </div>
      </div>
      {kycNotification && (
        <div className="dashboard-header" style={{ borderLeft: kycNotification.type === 'success' ? '4px solid #16a34a' : (kycNotification.type === 'error' ? '4px solid #dc2626' : '4px solid #2563eb'), background: '#f8fafc', marginBottom: 12 }}>
          <div className="header-content">
            <div>
              <h2 style={{ marginBottom: 4 }}>{kycNotification.title}</h2>
              <p>{kycNotification.message}</p>
            </div>
            <button className="refresh-btn" onClick={() => setKycNotification(null)}>Dismiss</button>
          </div>
        </div>
      )}
      {kycJustSubmitted && (
        <div className="dashboard-header" style={{ borderLeft: '4px solid #3b82f6', background: '#eff6ff', marginBottom: 12 }}>
          <div className="header-content">
            <div>
              <h2 style={{ marginBottom: 4 }}>KYC Submitted</h2>
              <p>Your KYC documents have been submitted successfully and are awaiting review.</p>
            </div>
          </div>
        </div>
      )}
      <div className="dashboard-header kyc-status-header">
        <div className="header-content">
          <div>
            <h2>KYC Status</h2>
            <p>
              {kycStatus === 'DRAFT' && '🟡 Draft — Please upload mandatory KYC documents to proceed.'}
              {kycStatus === 'SUBMITTED' && '🔵 Submitted — Your documents have been submitted and are awaiting review.'}
              {kycStatus === 'IN_REVIEW' && '🟠 In Review — Our team is verifying your documents.'}
              {(kycStatus === 'APPROVED' || kycStatus === 'VERIFIED' || kycStatus === 'ACTIVE') && '✅ Approved — You have full access to your dashboard.'}
              {kycStatus === 'REJECTED' && '❌ Rejected — Please re-upload the required documents as per feedback.'}
            </p>
            {kycStatus === 'REJECTED' && kycNotes && (
              <p style={{ color: '#991b1b', marginTop: 6 }}>Reason: {kycNotes}</p>
            )}
            {(kycStatus === 'APPROVED' || kycStatus === 'VERIFIED' || kycStatus === 'ACTIVE') ? (
              <div className="kyc-tracker">
                <div className="kyc-step active"><span className="dot" /> Approved</div>
              </div>
            ) : (
              <div className="kyc-tracker">
                <div className={`kyc-step ${['DRAFT'].includes(kycStatus) ? 'active' : 'done'}`}><span className="dot" /> Draft</div>
                <div className={`kyc-step ${kycStatus === 'SUBMITTED' ? 'active' : (['IN_REVIEW','APPROVED','VERIFIED','ACTIVE','REJECTED'].includes(kycStatus) ? 'done' : 'inactive')}`}><span className="dot" /> Submitted</div>
                <div className={`kyc-step ${kycStatus === 'IN_REVIEW' ? 'active' : (['APPROVED','VERIFIED','ACTIVE','REJECTED'].includes(kycStatus) ? 'done' : 'inactive')}`}><span className="dot" /> In Review</div>
                <div className={`kyc-step ${['APPROVED','VERIFIED','ACTIVE','REJECTED'].includes(kycStatus) ? 'active' : 'inactive'}`}><span className="dot" /> {kycStatus === 'REJECTED' ? 'Rejected' : 'Approved'}</div>
              </div>
            )}
          </div>
          <div className={`status-badge ${ (kycStatus || 'DRAFT').toLowerCase().replace(' ', '_') }`}>
            {kycStatus || 'DRAFT'}
          </div>
        </div>
      </div>
      <div className="dashboard-header">
        <div className="header-content">
          <div>
            <h1>Vendor Dashboard</h1>
            <p>Welcome back! Here's what's happening with your shop today.</p>
          </div>
          <div className="header-actions">
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
            <button 
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Weekly Revenue Widget */}
      <div className="widget-container">
        <div className="widget weekly-revenue">
          <div className="widget-header">
            <div className="widget-title">
              <IndianRupee size={20} />
              <h3>Weekly Revenue</h3>
            </div>
          <div className="widget-subtitle">This Week vs Last Week</div>
          <div>
            <a className="btn btn-sm btn-secondary" href="/api/metrics/weekly-revenue.csv">Export CSV</a>
          </div>
          </div>
          <div className="widget-content">
            <div className="revenue-main">
              <div className="revenue-amount">{formatCurrency(weeklyRevenue.current)}</div>
              <div className={`revenue-change ${weeklyRevenue.change > 0 ? 'positive' : 'negative'}`}>
                {weeklyRevenue.change > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                <span>{Math.abs(weeklyRevenue.change)}%</span>
              </div>
            </div>
            <div className="revenue-chart">
              {weeklyRevenue.dailyBreakdown.map((day, index) => (
                <div key={day.day} className="chart-bar">
                  <div 
                    className="bar-fill"
                    style={{ 
                      height: `${(day.amount / Math.max(...weeklyRevenue.dailyBreakdown.map(d => d.amount))) * 100}%` 
                    }}
                  />
                  <div className="bar-label">{day.day}</div>
                  <div className="bar-value">{formatCurrency(day.amount)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Orders by Status Widget */}
        <div className="widget orders-status">
          <div className="widget-header">
            <div className="widget-title">
              <ShoppingCart size={20} />
              <h3>Orders by Status</h3>
            </div>
          </div>
          <div className="widget-content">
            <div className="orders-grid">
              <div className="order-status-item">
                <div className="status-icon pending">
                  <Clock size={20} />
                </div>
                <div className="status-info">
                  <div className="status-count">{ordersByStatus.pending.count}</div>
                  <div className="status-label">Pending</div>
                  <div className="status-amount">{formatCurrency(ordersByStatus.pending.amount)}</div>
                  <div className={`status-change ${ordersByStatus.pending.changeCount >= 0 ? 'positive' : 'negative'}`}>
                    {ordersByStatus.pending.changeCount >= 0 ? '+' : ''}{ordersByStatus.pending.changeCount} vs last week
                  </div>
                </div>
              </div>
              <div className="order-status-item">
                <div className="status-icon shipped">
                  <Truck size={20} />
                </div>
                <div className="status-info">
                  <div className="status-count">{ordersByStatus.shipped.count}</div>
                  <div className="status-label">Shipped</div>
                  <div className="status-amount">{formatCurrency(ordersByStatus.shipped.amount)}</div>
                  <div className={`status-change ${ordersByStatus.shipped.changeCount >= 0 ? 'positive' : 'negative'}`}>
                    {ordersByStatus.shipped.changeCount >= 0 ? '+' : ''}{ordersByStatus.shipped.changeCount} vs last week
                  </div>
                </div>
              </div>
              <div className="order-status-item">
                <div className="status-icon delivered">
                  <CheckCircle size={20} />
                </div>
                <div className="status-info">
                  <div className="status-count">{ordersByStatus.delivered.count}</div>
                  <div className="status-label">Delivered</div>
                  <div className="status-amount">{formatCurrency(ordersByStatus.delivered.amount)}</div>
                  <div className={`status-change ${ordersByStatus.delivered.changeCount >= 0 ? 'positive' : 'negative'}`}>
                    {ordersByStatus.delivered.changeCount >= 0 ? '+' : ''}{ordersByStatus.delivered.changeCount} vs last week
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Alerts Widget */}
      <div className="widget inventory-alerts">
        <div className="widget-header">
          <div className="widget-title">
            <AlertTriangle size={20} />
            <h3>Inventory Alerts</h3>
          </div>
          <div className="widget-subtitle">Low Stock & Out of Stock Items</div>
        </div>
        <div className="widget-content">
          <div className="alerts-list">
            {inventoryAlerts.map((alert) => (
              <div key={alert.id} className="alert-item">
                <div className="alert-priority" style={{ backgroundColor: getPriorityColor(alert.priority) }} />
                <div className="alert-content">
                  <div className="alert-product">{alert.product}</div>
                  <div className="alert-details">
                    <span>Stock: {alert.currentStock}</span>
                    <span>Min: {alert.minStock}</span>
                  </div>
                </div>
                <div className="alert-status">
                  <span className={`status-badge ${getStatusBadge(alert.status)}`}>
                    {alert.status}
                  </span>
                </div>
                <div className="alert-actions">
                  <button className="btn btn-sm btn-primary" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'inventory' } }))}>Restock</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payout Summary Widget */}
      <div className="widget payout-summary">
        <div className="widget-header">
          <div className="widget-title">
            <IndianRupee size={20} />
            <h3>Payout Summary</h3>
          </div>
          <div className="widget-subtitle">Earnings & Payment Information</div>
        </div>
        <div className="widget-content">
          <div className="payout-grid">
            <div className="payout-item">
              <div className="payout-label">Total Earnings</div>
              <div className="payout-value primary">{formatCurrency(payoutSummary.totalEarnings)}</div>
            </div>
            <div className="payout-item">
              <div className="payout-label">Pending Payout</div>
              <div className="payout-value warning">{formatCurrency(payoutSummary.pendingPayout)}</div>
            </div>
            <div className="payout-item">
              <div className="payout-label">Last Payout</div>
              <div className="payout-value success">{formatCurrency(payoutSummary.lastPayout)}</div>
            </div>
            <div className="payout-item">
              <div className="payout-label">Next Payout</div>
              <div className="payout-value info">{payoutSummary.nextPayoutDate}</div>
            </div>
          </div>
          <div className="payout-method">
            <span>Payment Method: {payoutSummary.payoutMethod}</span>
          </div>
        </div>
      </div>

      {/* KYC Documents Widget */}
      <div className="widget kyc-documents">
        <div className="widget-header">
          <div className="widget-title">
            <Package size={20} />
            <h3>KYC Documents</h3>
          </div>
          <div className="widget-subtitle">View, replace, or delete your documents</div>
        </div>
        <div className="widget-content">
          {kycDocs.length === 0 ? (
            <div style={{ padding: 12, color: '#475569' }}>No documents uploaded yet. Go to the KYC tab to upload.</div>
          ) : (
            <div className="orders-grid">
              {kycDocs.map(doc => (
                <div key={doc.id} className="order-status-item" style={{ alignItems: 'center' }}>
                  <div className="status-icon shipped">
                    <FileIcon mime={doc.mimeType} />
                  </div>
                  <div className="status-info" style={{ flex: 1 }}>
                    <div className="status-count" style={{ fontSize: 14 }}>{doc.label}</div>
                    <div className="status-label" title={doc.originalName}>{doc.originalName}</div>
                    <div className="status-amount" style={{ fontSize: 12 }}>{new Date(doc.uploadedAt).toLocaleString()}</div>
                  </div>
                  <div className="alert-actions" style={{ display: 'flex', gap: 8 }}>
                    <a className="btn btn-sm btn-secondary" href={doc.absoluteUrl || doc.url} target="_blank" rel="noreferrer">
                      <Eye size={16} />
                    </a>
                    <button className="btn btn-sm btn-primary" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { tab: 'kyc' } }))}>
                      <Edit3 size={16} />
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteDoc(doc.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
